import { LoanStatus } from '@nemuru-eng/js-api-client';

import { loanApplicationEditModeMapper } from '@/components/shared/components/ConditionsSummary/LoanConditionsSummary/components/LenderApplications/mappers/loanApplicationEditModeMapper';
import { LoanApplicationAction } from '@/components/shared/components/ConditionsSummary/LoanConditionsSummary/components/LenderApplications/types/LoanApplicationAction';
import { LoanApplicationCapability } from '@/components/shared/components/ConditionsSummary/LoanConditionsSummary/components/LenderApplications/types/LoanApplicationCapability';
import { LoanApplicationMode } from '@/components/shared/components/ConditionsSummary/LoanConditionsSummary/components/LenderApplications/types/LoanApplicationMode';
import { Text } from '@/components/shared/components/Text';
import { ENV_AWS_S3_BUCKET_URL } from '@/config/envVars';

interface UseLoanApplicationHeaderActionsProps {
  loanId: string;
  loanStatus: LoanStatus;
  lenderName: string;
  isLoanLenderBorrowerContractSent?: boolean;
  onClickAction: (action: LoanApplicationAction) => void;
}

export const useLoanApplicationHeaderActions = ({
  loanId,
  loanStatus,
  lenderName,
  isLoanLenderBorrowerContractSent,
  onClickAction,
}: UseLoanApplicationHeaderActionsProps) => {
  const openProcessResponseFolder = {
    iconName: 'open_in_new',
    display: <Text tId={'OPEN_PROCESS_RESPONSE_FOLDER'} />,
    onClick: () =>
      window.open(`${ENV_AWS_S3_BUCKET_URL}&prefix=loans/${loanId}/lenders/${lenderName}/&showversions=false`),
  };

  const changeLenderApprovalStatus = {
    iconName: 'account_balance',
    display: <Text tId={'CHANGE_LENDER_APPROVAL_STATUS'} />,
    onClick: () => onClickAction(LoanApplicationAction.CHANGE_LENDER_APPROVAL_STATUS),
  };

  const setLenderRequestId = {
    iconName: 'edit',
    display: <Text tId={'SET_LENDER_REQUEST_ID'} />,
    onClick: () => onClickAction(LoanApplicationAction.SET_LENDER_REQUEST_ID),
  };

  const resendContractSignatureToBorrower = {
    iconName: 'draw',
    display: <Text tId={'RESEND_CONTRACT_SIGNATURE_TO_BORROWER'} />,
    onClick: () => onClickAction(LoanApplicationAction.RESEND_CONTRACT_SIGNATURE_TO_BORROWER),
  };

  const sendContractSignatureToBorrower = {
    iconName: 'draw',
    display: <Text tId={'SEND_CONTRACT_SIGNATURE_TO_BORROWER'} />,
    onClick: () => onClickAction(LoanApplicationAction.SEND_CONTRACT_SIGNATURE_TO_BORROWER),
  };

  const setContractSigned = {
    display: <Text tId={'SET_CONTRACT_SIGNED'} />,
    onClick: () => onClickAction(LoanApplicationAction.SET_CONTRACT_SIGNED),
    iconName: 'check',
  };

  const unSignContract = {
    iconName: 'block',
    display: <Text tId={'UN_SIGN_LOAN_LENDER_BORROWER_CONTRACT'} />,
    onClick: () => onClickAction(LoanApplicationAction.UN_SIGN_CONTRACT),
  };

  const isStatusLenderHasBeenSelected = loanStatus === LoanStatus.STATUS_LENDER_HAS_BEEN_SELECTED;
  const isStatusContractSigned = loanStatus === LoanStatus.STATUS_CONTRACT_SIGNED;

  const getActionOptionsArray = (mode: LoanApplicationMode) => {
    const loanApplicationCapabilities = loanApplicationEditModeMapper(mode);

    return loanApplicationCapabilities
      .flatMap((capability) => {
        switch (capability) {
          case LoanApplicationCapability.OPEN_PROCESS_RESPONSE_FOLDER:
            return openProcessResponseFolder;
          case LoanApplicationCapability.CHANGE_LENDER_APPROVAL_STATUS:
            return changeLenderApprovalStatus;
          case LoanApplicationCapability.SET_LENDER_REQUEST_ID:
            return setLenderRequestId;
          case LoanApplicationCapability.SET_CONTRACT_SIGNED:
            return isStatusLenderHasBeenSelected ? setContractSigned : undefined;
          case LoanApplicationCapability.CONTRACT_SIGNATURE_TO_BORROWER_SENDING:
            return isStatusLenderHasBeenSelected
              ? isLoanLenderBorrowerContractSent
                ? resendContractSignatureToBorrower
                : sendContractSignatureToBorrower
              : undefined;
          case LoanApplicationCapability.UN_SIGN_CONTRACT:
            return isStatusContractSigned ? unSignContract : undefined;
        }
      })
      .filter((el) => el !== undefined);
  };

  return { getActionOptionsArray };
};
